import { useEffect, useState } from "react";
import { apiClient } from "./client.js";
import { SnapshotDiffResponseSchema, type SnapshotDiffResponse } from "./types.js";
import { pollInboxUntilDone } from "../components/asset-history-tab.js";

// 3.B.3.3: useSnapshotDiff-hook.
//
// Pyytää daemon-inbox:lta snapshot-diff:n (snapshotIdA vs snapshotIdB,
// optional filter). Daemon-puolen 5 min LRU-cache nopeuttaa toistuvia
// kyselyitä (saman session slider-pysähdykset). Web-puolelle ylimääräinen
// muisti-cache jotta unmount → remount ei tuhlaa uutta kyselyä.

type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "completed"; data: SnapshotDiffResponse }
  | { status: "failed"; error: string };

const DIFF_TIMEOUT_MS = 90_000;

// Yksinkertainen muisti-cache (max 20 entryä, sama kuin daemon-puolella)
const webCache = new Map<string, SnapshotDiffResponse>();
const CACHE_MAX = 20;

function cacheKey(machineId: string, a: string, b: string, filter: string | null): string {
  return `${machineId}|${a}|${b}|${filter ?? ""}`;
}

function cacheGet(key: string): SnapshotDiffResponse | null {
  const hit = webCache.get(key);
  if (!hit) return null;
  webCache.delete(key);
  webCache.set(key, hit);
  return hit;
}

function cacheSet(key: string, value: SnapshotDiffResponse): void {
  if (webCache.size >= CACHE_MAX) {
    const oldest = webCache.keys().next().value;
    if (oldest !== undefined) webCache.delete(oldest);
  }
  webCache.set(key, value);
}

export function useSnapshotDiff(
  machineId: string | null,
  snapshotIdA: string | null,
  snapshotIdB: string | null,
  sourcePathFilter: string | null
): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!machineId || !snapshotIdA || !snapshotIdB) {
      setState({ status: "idle" });
      return;
    }
    if (snapshotIdA === snapshotIdB) {
      // Sama snapshot vs itseään → tyhjä diff (ei haeta daemonilta)
      setState({
        status: "completed",
        data: {
          added: [],
          modified: [],
          removed: [],
          summary: {
            addedCount: 0,
            modifiedCount: 0,
            removedCount: 0,
            addedBytes: 0,
            modifiedBytes: 0,
            removedBytes: 0,
          },
        },
      });
      return;
    }

    const key = cacheKey(machineId, snapshotIdA, snapshotIdB, sourcePathFilter);
    const cached = cacheGet(key);
    if (cached) {
      setState({ status: "completed", data: cached });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });
    (async () => {
      try {
        const request: Record<string, unknown> = {
          snapshotIdA,
          snapshotIdB,
        };
        if (sourcePathFilter) request["sourcePathFilter"] = sourcePathFilter;
        const created = await apiClient.postInboxRequest(machineId, {
          type: "snapshot-diff",
          request,
        });
        const result = await pollInboxUntilDone(created.id, DIFF_TIMEOUT_MS);
        if (cancelled) return;
        if (result.status === "completed") {
          const parsed = SnapshotDiffResponseSchema.safeParse(result.result);
          if (!parsed.success) {
            setState({ status: "failed", error: "Diff-vastaus virheellistä muotoa" });
            return;
          }
          cacheSet(key, parsed.data);
          setState({ status: "completed", data: parsed.data });
        } else {
          setState({
            status: "failed",
            error: result.error ?? `Diff ${result.status}`,
          });
        }
      } catch (err) {
        if (cancelled) return;
        setState({
          status: "failed",
          error: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [machineId, snapshotIdA, snapshotIdB, sourcePathFilter]);

  return state;
}
